import { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useTelegram } from '@/hooks/useTelegram';

interface LastBreathModalProps {
  isOpen: boolean;
  characterName: string;
  onRollResult: (result: number, outcome: 'survive' | 'bargain' | 'death') => void;
}

export function LastBreathModal({ isOpen, characterName, onRollResult }: LastBreathModalProps) {
  const { hapticFeedback } = useTelegram();
  const [isRolling, setIsRolling] = useState(false);
  const [dice1, setDice1] = useState<number | null>(null);
  const [dice2, setDice2] = useState<number | null>(null);
  const [total, setTotal] = useState<number | null>(null);
  const [outcome, setOutcome] = useState<'survive' | 'bargain' | 'death' | null>(null);

  const handleRoll = () => {
    if (isRolling) return;

    setIsRolling(true);
    hapticFeedback('heavy');

    // Animate dice rolling
    let rollCount = 0;
    const maxRolls = 15;

    const rollInterval = setInterval(() => {
      const d1 = Math.floor(Math.random() * 6) + 1;
      const d2 = Math.floor(Math.random() * 6) + 1;
      setDice1(d1);
      setDice2(d2);
      rollCount++;

      if (rollCount >= maxRolls) {
        clearInterval(rollInterval);

        // Final roll
        const finalD1 = Math.floor(Math.random() * 6) + 1;
        const finalD2 = Math.floor(Math.random() * 6) + 1;
        const finalTotal = finalD1 + finalD2;

        setDice1(finalD1);
        setDice2(finalD2);
        setTotal(finalTotal);

        // Determine outcome
        let result: 'survive' | 'bargain' | 'death';
        if (finalTotal >= 10) {
          result = 'survive';
          hapticFeedback('success');
        } else if (finalTotal >= 7) {
          result = 'bargain';
          hapticFeedback('medium');
        } else {
          result = 'death';
          hapticFeedback('error');
        }

        setOutcome(result);
        setIsRolling(false);

        // Notify parent after a delay
        setTimeout(() => {
          onRollResult(finalTotal, result);
        }, 2500);
      }
    }, 80);
  };

  const getOutcomeText = () => {
    if (!outcome) return null;

    switch (outcome) {
      case 'survive':
        return {
          title: 'Вы вернулись!',
          description: 'Смерть отступила. Вы приходите в себя с 1 HP.',
          color: 'text-green-400',
          bgColor: 'bg-green-500/20',
          borderColor: 'border-green-500/30',
        };
      case 'bargain':
        return {
          title: 'Смерть предлагает сделку...',
          description: 'Вы стоите на пороге. Смерть готова вас отпустить, но у неё есть условие.',
          color: 'text-amber-400',
          bgColor: 'bg-amber-500/20',
          borderColor: 'border-amber-500/30',
        };
      case 'death':
        return {
          title: 'Конец пути',
          description: `${characterName} погибает. Тьма поглощает вас...`,
          color: 'text-red-400',
          bgColor: 'bg-red-500/20',
          borderColor: 'border-red-500/30',
        };
    }
  };

  const diceDisplay = (value: number | null) => {
    const dots: Record<number, string> = {
      1: '⚀',
      2: '⚁',
      3: '⚂',
      4: '⚃',
      5: '⚄',
      6: '⚅',
    };
    return value ? dots[value] : '🎲';
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/90 z-50 flex items-center justify-center p-4"
        >
          <motion.div
            initial={{ scale: 0.8, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.8, opacity: 0 }}
            className="bg-gray-900 rounded-2xl p-6 max-w-sm w-full border border-red-900/50"
          >
            {/* Header */}
            <div className="text-center mb-6">
              <motion.span
                animate={{
                  scale: [1, 1.2, 1],
                  opacity: [0.5, 1, 0.5],
                }}
                transition={{
                  duration: 2,
                  repeat: Infinity,
                  ease: 'easeInOut',
                }}
                className="text-5xl block mb-3"
              >
                💀
              </motion.span>
              <h2 className="text-2xl font-bold text-red-400 mb-2">Последний Вздох</h2>
              <p className="text-gray-400 text-sm">
                {characterName} на грани смерти. Бросьте 2d6, чтобы узнать свою судьбу.
              </p>
            </div>

            {/* Dice display */}
            <div className="flex justify-center gap-4 mb-6">
              <motion.div
                animate={isRolling ? { rotate: [0, 360] } : {}}
                transition={{ duration: 0.3, repeat: isRolling ? Infinity : 0 }}
                className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center text-4xl border border-gray-700"
              >
                {diceDisplay(dice1)}
              </motion.div>
              <motion.div
                animate={isRolling ? { rotate: [360, 0] } : {}}
                transition={{ duration: 0.3, repeat: isRolling ? Infinity : 0 }}
                className="w-16 h-16 bg-gray-800 rounded-xl flex items-center justify-center text-4xl border border-gray-700"
              >
                {diceDisplay(dice2)}
              </motion.div>
            </div>

            {/* Total */}
            {total !== null && (
              <motion.div
                initial={{ scale: 0, opacity: 0 }}
                animate={{ scale: 1, opacity: 1 }}
                className="text-center mb-4"
              >
                <span className="text-4xl font-bold text-white">{total}</span>
              </motion.div>
            )}

            {/* Outcome message */}
            {outcome && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className={`p-4 rounded-xl ${getOutcomeText()?.bgColor} border ${getOutcomeText()?.borderColor} mb-4`}
              >
                <p className={`font-bold ${getOutcomeText()?.color} mb-1`}>
                  {getOutcomeText()?.title}
                </p>
                <p className="text-gray-300 text-sm">
                  {getOutcomeText()?.description}
                </p>
              </motion.div>
            )}

            {/* Roll button */}
            {!outcome && (
              <motion.button
                onClick={handleRoll}
                disabled={isRolling}
                whileTap={{ scale: 0.95 }}
                className={`w-full py-4 rounded-xl font-bold text-lg transition-all ${
                  isRolling
                    ? 'bg-gray-700 text-gray-400 cursor-not-allowed'
                    : 'bg-red-600 text-white hover:bg-red-500'
                }`}
              >
                {isRolling ? 'Бросок...' : '🎲 Бросить кости'}
              </motion.button>
            )}

            {/* Legend */}
            {!outcome && (
              <div className="mt-4 text-xs text-gray-500 text-center space-y-1">
                <p><span className="text-green-400">10+</span> — Возвращение к жизни</p>
                <p><span className="text-amber-400">7-9</span> — Смерть предлагает сделку</p>
                <p><span className="text-red-400">6-</span> — Конец пути</p>
              </div>
            )}
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}
